import { AfterViewChecked, Component, ElementRef, ViewChild } from '@angular/core'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { AssistantConversationService } from '../../services/assistant-conversation.service'
import { AssistantMessageRole, IAssistantMessage } from '../../models/assistant.model'

/** A single rendered row in the stream — a real conversation message. */
interface IChatStreamItem {
  role: AssistantMessageRole
  text: string
  error?: boolean
  muted?: boolean
  createdAt?: Date
}

/**
 * The assistant message stream — the dock's body (ux-command-bar-spec.md → §the escalation ladder).
 * Purely presentational: custom message bubbles + the inline confirmation card + a "working" affordance,
 * one scroll container. The composer and all backend seams (ensureConversation, client-tool connect,
 * send) live in `<assistant-bar-active>` (the persistent bar), so this panel opens no listeners itself
 * beyond the live `messages$` it renders. Message bodies reuse `<ui-message-markdown>` for markdown parity.
 *
 * The guided walkthrough is its own floating surface (`<tour-card>`) and no longer renders here — this
 * stream shows only real conversation messages.
 */
@Component({
  selector: 'assistant-chat-panel',
  template: `
    <div #stream (scroll)="onStreamScroll()" class="e11-h-full e11-min-h-0 e11-overflow-y-auto e11-flex e11-flex-col e11-gap-4 e11-pr-1">
      <ng-container *ngrxLet="items$ as items">
        <!-- Empty zero-state -->
        <e11-empty-state
          *ngIf="items.length === 0"
          icon="smart_toy"
          iconType="outlined"
          [border]="''"
          [clickable]="false"
          [title]="'Assistant' | translate"
          [subtitle]="'Ask the assistant anything about your hiring' | translate"
        ></e11-empty-state>

        <!-- Message bubbles. Internal run steps are intentionally NOT rendered here (they were
             low-signal "Done" status rows); the client-tool executor still consumes steps$ from the
             service. In-progress state is conveyed by the working affordance below. -->
        <ng-container *ngFor="let message of items">
          <div
            class="e11-rounded-md e11-flex e11-flex-col e11-gap-2 e11-px-3 e11-py-2"
            [ngClass]="{
              'e11-bg-skin-primary/5': message.role === AssistantMessageRole.User,
              'e11-bg-skin-app-bg': message.role !== AssistantMessageRole.User,
              'e11-text-skin-error': message.error,
              'e11-text-gray-500': message.muted && !message.error
            }"
          >
            <ui-message-markdown [message]="$any({ text: message.text, mentions: {} })"></ui-message-markdown>
          </div>
        </ng-container>

        <!-- Confirmation card (design-contract.md → §Surface 4). Self-manages visibility from the
             live pending-action doc (story 4.8). -->
        <assistant-confirmation-card></assistant-confirmation-card>

        <!-- Assistant-working affordance (only while a run is actively in progress, not on Error/Idle) -->
        <div *ngIf="working$ | async" class="e11-flex e11-items-center e11-gap-2 e11-text-gray-500">
          <e11-loader-bouncing-dots [size]="'sm'"></e11-loader-bouncing-dots>
          <span class="e11-text-xs">{{ 'The assistant is working…' | translate }}</span>
        </div>
      </ng-container>
    </div>
  `,
})
export class AssistantChatPanelComponent implements AfterViewChecked {
  @ViewChild('stream') stream?: ElementRef<HTMLDivElement>

  AssistantMessageRole = AssistantMessageRole

  working$ = this.assistantService.working$

  /** The rendered stream: the live conversation messages. */
  items$: Observable<IChatStreamItem[]> = this.assistantService.messages$.pipe(
    startWith([] as IAssistantMessage[]),
    map(messages => messages.map(m => ({ role: m.role, text: m.text, error: m.error, muted: m.muted, createdAt: m.createdAt })))
  )

  /** Stick to the bottom unless the user has scrolled up to read history. */
  private autoScroll = true
  private lastScrollHeight = 0

  constructor(private assistantService: AssistantConversationService) {}

  ngAfterViewChecked(): void {
    const el = this.stream?.nativeElement
    if (!el) return
    if (this.autoScroll && el.scrollHeight !== this.lastScrollHeight) {
      el.scrollTop = el.scrollHeight
      this.lastScrollHeight = el.scrollHeight
    }
  }

  onStreamScroll(): void {
    const el = this.stream?.nativeElement
    if (!el) return
    // "Near bottom" (within 24px) re-arms auto-scroll; scrolling up disables it.
    this.autoScroll = el.scrollHeight - el.scrollTop - el.clientHeight < 24
  }
}
